import type { BoxProps, ButtonProps, IconProp } from "@/theme"

import React from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet } from "react-native"

import { clickCancel } from "@/redux/actions"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { Box, Button, Row, useAppThemeContext } from "@/theme"

export interface ActionButtonProps extends BoxProps {
  SubmitIconComponent?: IconProp
  SubmitIconPosition?: ButtonProps["iconPosition"]
  buttonSize?: ButtonProps["size"]
  cancelText?: string
  isCancelDisabled?: boolean
  isDisabled?: boolean
  isLoading: boolean
  isSubmitDisabled?: boolean
  onPressCancel?: () => unknown
  onPressSubmit: () => unknown
  orientation?: "horizontal" | "vertical"
  submitText?: string
}

const styles = StyleSheet.create({
  common: {
    flexGrow: 1,
    minWidth: 120,
  },
})

export function ActionButtons({
  buttonSize = "lg",
  cancelText,
  isCancelDisabled = false,
  isDisabled = false,
  isLoading,
  isSubmitDisabled = false,
  mt,
  onPressCancel,
  onPressSubmit,
  orientation,
  SubmitIconComponent,
  SubmitIconPosition,
  submitText,
  zIndex,
  ...rest
}: ActionButtonProps): React.JSX.Element {
  // console.log({ zIndex })
  const dispatch = useRootDispatch()
  const { t } = useTranslation()

  const submitButton = (overrides?: ButtonProps) => (
    <Button
      IconComponent={SubmitIconComponent}
      iconPosition={SubmitIconPosition}
      id={"submitBtn"}
      isDisabled={isLoading || isSubmitDisabled || isDisabled}
      isLoading={isLoading}
      size={buttonSize}
      style={[styles.common, { zIndex }]}
      text={submitText ?? t("submit")}
      variant={"primary"}
      onPress={onPressSubmit}
      {...overrides}
    />
  )
  const cancelButton = (overrides?: ButtonProps) => (
    <Button
      id={"cancelBtn"}
      isDisabled={isLoading || isCancelDisabled || isDisabled}
      size={buttonSize}
      style={[styles.common, { zIndex }]}
      text={cancelText ?? t("cancel")}
      variant={"outline"}
      onPress={onPressCancel ?? (() => dispatch(clickCancel()))}
      {...overrides}
    />
  )
  const breakpointKey = useAppThemeContext().screenSize
  const isSmallScreen = breakpointKey === "xs"

  if (
    orientation === "vertical" ||
    (isSmallScreen && orientation !== "horizontal")
  ) {
    return (
      <Box mt={mt} {...rest}>
        <Box mb={"$4"}>{submitButton()}</Box>
        {cancelButton({ variant: "text" })}
      </Box>
    )
  }
  return (
    <Row
      alignItems={"center"}
      flexWrap={"nowrap"}
      mt={mt}
      zIndex={zIndex}
      {...rest}
    >
      <Box flexGrow={1} mr={"$4"} zIndex={zIndex}>
        {cancelButton(isSmallScreen ? { size: "lg" } : undefined)}
      </Box>
      <Box flexGrow={1} zIndex={zIndex}>
        {submitButton(isSmallScreen ? { size: "lg" } : undefined)}
      </Box>
    </Row>
  )
}
